import { IPageLink, IUser } from '@vivli/shared/infrastructure/interface';
import { AssignedAppTypeEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import i18next from 'i18next';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useOrganizationsApiContext,
    useAuthServiceContext
} from '@vivli/core/infrastructure/context';
import { useEffect, useState } from 'react';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { useTranslation } from 'react-i18next';

export function useUserRouteMapperCtHook() {
    const { isDatasetOwner, isOrgOrVivliAdmin, isDataProvider, isAnnotator, isQaReviewer, isIRPApprover, isPresumedResearcher } =
        useUserPermissions();
    const { userHasPermission } = useActiveUserContext();
    const { t } = useTranslation();
    const isDataProviderEnq = isDataProvider;
    const { isAmr } = useAssignedAppTypeContext();
    const [organizations, setOrganizations] = useState<IOrganization[]>();
    const organizationsService = useOrganizationsApiContext();

    //we have to look at the org types the user has because we want to hide
    //the "Study Submissions" tab for users who are affiliated with industry orgs
    useEffect(() => {
        const sub = organizationsService.getOrganizations().subscribe(setOrganizations);
        return () => {
            sub.unsubscribe();
        };
    }, []);

    const order = [
        'Dashboard',
        'Reports',
        'Data Requests',
        'Enquiries',
        'Studies',
        'Research Environments',
        'My Organizations',
        'My Organization',
        'Users',
        'Study Submissions',
        'Admin Console'
    ];
    const getUserRoutes = (
        user: IUser //user here is logged in user
    ): Array<{
        name?: string;
        title?: string;
        icon?: { active: string; inactive: string };
        url?: string;
        defaultTab?: string;
        subLinks?: IPageLink[];
        managementTab?: boolean; //tab used for "management" only, meaning won't appear on top menu
        //appears on right-hand menu instead
    }> => {
        if (!user) {
            return [];
        }
        //TODO PERMFE - redo with permissions+
        const orgs = user?.orgMemberships?.filter((om) => om.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv));

        let orgId = null;
        if (orgs && orgs.length !== 0) {
            [{ orgId }] = orgs;
        }

        const links: Array<{
            name?: string;
            title?: string;
            icon?: { active: string; inactive: string };
            url?: string;
            defaultTab?: string;
            managementTab?: boolean; //tab used for "management" only, meaning won't appear on top menu
            //appears on right-hand menu instead
        }> = [
            {
                title: 'Dashboard',
                icon: {
                    active: AssetsConstant.DASHBOARD_ICON_ACTIVE,
                    inactive: AssetsConstant.DASHBOARD_ICON_INACTIVE
                },
                url: 'dashboard',
                defaultTab: ''
            },
            {} // empty placeholder key for admin portal side nav panels
        ];

        //TODO PERMFE - redo with permissions+
        const isVivliAdmin = userHasPermission(user, PermissionsEnum.DrActAdv);
        const isOpsAdmin = userHasPermission(user, PermissionsEnum.UseAdmConsoleAdv);

        //user with no orgs is allowed to see "Study Submissions" tab
        //as are vivli and ops admins
        if (!user.orgMemberships?.some((a) => a.isEnabled) || isVivliAdmin) {
            links.splice(1, 0, {
                title: 'Study Submissions',
                icon: {
                    active: AssetsConstant.TRIALS_ICON_ACTIVE,
                    inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                },
                url: `study-submissions`,
                defaultTab: 'draft'
            });
        }

        if (user.orgMemberships) {
            //user in academic or other non-industry org only can see "Study Submissions" tab
            if (organizations && !isVivliAdmin) {
                const userOrgs = organizations.filter((org) => user.orgMemberships?.map((om) => om.orgId).includes(org.id));

                if (userOrgs?.some((o) => o.type !== 'Industry')) {
                    links.splice(1, 0, {
                        title: 'Study Submissions',
                        icon: {
                            active: AssetsConstant.TRIALS_ICON_ACTIVE,
                            inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                        },
                        url: `study-submissions`,
                        defaultTab: 'draft'
                    });
                }
            }

            if (isDataProvider || isIRPApprover || isPresumedResearcher) {
                links.splice(1, 0, {
                    title: 'Data Requests',
                    icon: {
                        active: AssetsConstant.REQUESTS_ICON_ACTIVE,
                        inactive: AssetsConstant.REQUESTS_ICON_INACTIVE
                    },
                    url: 'requests',
                    defaultTab: isPresumedResearcher ? 'draft' : 'AwaitingMyAction'
                });
            }
            if (isAnnotator || isQaReviewer || isDataProvider || isOrgOrVivliAdmin) {
                links.splice(1, 0, {
                    title: t('studies'),
                    icon: {
                        active: AssetsConstant.TRIALS_ICON_ACTIVE,
                        inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                    },
                    url: 'studies',
                    defaultTab: ''
                });
            }

            //TODO PERMFE - redo with permissions+
            if (isOrgOrVivliAdmin) {
                links.splice(1, 0, {
                    title: 'Organizations',
                    icon: {
                        active: AssetsConstant.OFFICEBUILDING_ICON_ACTIVE,
                        inactive: AssetsConstant.OFFICEBUILDING_ICON_INACTIVE
                    },
                    url: !isVivliAdmin && !(orgs?.length > 1) ? `orgs/${orgId}` : 'orgs',
                    defaultTab: '',
                    managementTab: true
                });
                links.splice(1, 0, {
                    title: 'Reports',
                    icon: {
                        active: AssetsConstant.TRIALS_ICON_ACTIVE,
                        inactive: AssetsConstant.TRIALS_ICON_INACTIVE
                    },
                    url: `report`,
                    defaultTab: ''
                });
                links.splice(1, 0, {
                    title: 'Users',
                    icon: {
                        active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                        inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                    },
                    url: `users`,
                    defaultTab: '',
                    managementTab: true
                });
            }

            if (isVivliAdmin || (isOrgOrVivliAdmin && isDataProvider)) {
                links.splice(1, 0, {
                    title: 'Research Environments',
                    icon: {
                        active: AssetsConstant.VM_ICON_ACTIVE,
                        inactive: AssetsConstant.VM_ICON_INACTIVE
                    },
                    url: `vms`,
                    defaultTab: '',
                    managementTab: true
                });
            }
        } else {
            //TODO PERMFE - redo with permissions+
            if (isVivliAdmin || isOpsAdmin) {
                links.splice(1, 0, {
                    title: 'Organizations',
                    icon: {
                        active: AssetsConstant.OFFICEBUILDING_ICON_ACTIVE,
                        inactive: AssetsConstant.OFFICEBUILDING_ICON_INACTIVE
                    },
                    url: !isVivliAdmin && !isOrgOrVivliAdmin ? `orgs/${orgId}` : 'orgs',
                    defaultTab: '',
                    managementTab: true
                });
                links.splice(1, 0, {
                    title: 'Users',
                    icon: {
                        active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                        inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                    },
                    url: `users`,
                    defaultTab: '',
                    managementTab: true
                });
                links.splice(1, 0, {
                    title: 'Research Environments',
                    icon: {
                        active: AssetsConstant.NAV_VM_ICON,
                        inactive: AssetsConstant.NAV_VM_ICON
                    },
                    url: `vms`,
                    defaultTab: '',
                    managementTab: true
                });
            }
        }

        let isRelevantOther = false;
        if (isAnnotator || isIRPApprover || isQaReviewer) {
            isRelevantOther = true; // this had to be simplified to accomodate TypeScript limitations
        }

        //Note: per Dev-275, removing restrictions on showing Enquiries tab
        //leaving commented code for the moment for reference until this is reviewed/finalized
        //TODO - remove commented code below
        //let canSeeEnquiries = true;

        // if (
        //     (isDataProviderEnq && !isOrgOrVivliAdmin && !isVivliAdmin) ||
        //     (isRelevantOther && !isDataProviderEnq && !isVivliAdmin) ||
        //     (isIRPApprover && !isOrgOrVivliAdmin)
        // ) {
        //     canSeeEnquiries = false;
        // }
        //if (canSeeEnquiries) {

        links.splice(links.length - 1, 0, {
            title: 'Enquiries',
            icon: {
                active: AssetsConstant.TRIALS_ICON_ACTIVE,
                inactive: AssetsConstant.TRIALS_ICON_INACTIVE
            },
            url: 'enquiries',
            defaultTab: ''
        });
        //}

        if (isOpsAdmin) {
            links.splice(links.length - 1, 0, {
                title: 'Admin Console',
                icon: {
                    active: AssetsConstant.ORGANIZATIONS_ICON_ACTIVE,
                    inactive: AssetsConstant.ORGANIZATIONS_ICON_INACTIVE
                },
                url: `adminConsole/templateStudy`,
                defaultTab: 'templateStudy',
                managementTab: true
            });
        }

        //remove any links that are empty placeholders or that the user doesn't have permission to see (in case of any misconfigurations)
        const filteredLinks = links.filter((link) => {
            if (!link.title) return false; // remove empty placeholders
            return true;
        });

        filteredLinks.sort((a, b) => {
            if (!a.title) return 1;
            if (!b.title) return -1;

            const indexA = order.indexOf(a.title);
            const indexB = order.indexOf(b.title);
            return indexA - indexB;
        });

        return filteredLinks;
    };

    return {
        getUserRoutes
    };
}
